import './main.css';

import { createRoot, Root, RootOptions } from 'react-dom/client';

import {
    CommunicationChannelsErrorDialog,
    CommunicationChannelsErrorDialogProps,
} from '@/dialogs/communication-channels-error/CommunicationChannelsErrorDialog';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { PriorItemDialog, PriorItemDialogProps} from '@/dialogs/prior-item/PriorItemDialog.tsx';

import { ItemDetails, ItemDetailsComponentProps } from './components/ItemDetails/ItemDetails.tsx';
import { ItemsList, ItemsListComponentProps } from './components/itemsList/itemsList.tsx';
import { ReactBridgePageWrapper } from './components/ReactBridgePageWrapper.tsx';
import {
    CompareItemsDialog,
    CompareItemsDialogProps,
} from './dialogs/compare-items/CompareItemsDialog.tsx';
import { CreateTaskDialog, CreateTaskDialogProps } from './dialogs/create-task/CreateTaskDialog.tsx';
import {
    DataModelExchangeDialog,
    DataModelExchangeDialogProps,
} from './dialogs/data-model-exchange/DataModelExchangeDialog.tsx';
import { openPublishToRecipients } from './dialogs/dialogHostBridge.tsx';
import {
    ExportMediaAssetDialog,
    ExportMediaAssetDialogProps,
} from './dialogs/export-media-asset/ExportMediaAssetDialog.tsx';
import { FocusAttributeProvider } from './dialogs/item-editor/Context/FocusAttributeProvider.tsx';
import { ItemEditorDialog, ItemEditorDialogProps } from './dialogs/item-editor/ItemEditorDialog.tsx';
import { ItemReviewsDialog, ItemReviewsDialogProps } from './dialogs/item-reviews/ItemReviewsDialog.tsx';
import { MassUpdateDialog, MassUpdateDialogProps } from './dialogs/mass-update/MassUpdateDialog.tsx';
import {
    PublishToRecipientsDialog,
    PublishToRecipientsDialogProps,
} from './dialogs/publish-to-recipients/PublishToRecipientsDialog.tsx';
import AdminSettingsPage from './pages/admin-settings/AdminSettingsPage';
import { BrowsePage, SearchFilterBarHost } from './pages/browse';
import type { BrowsePageRouteProps } from './pages/browse/browseRoute';
import { FilterBuilder, FilterBuilderProps } from './pages/browse/components/SearchFilterBar/FilterBuilder';
import BulkSubscriptionsPage from './pages/bulk-subscriptions/BulkSubscriptionsPage.tsx';
import { ContactsPage } from './pages/contact/ContactsPage';
import { SendEmailInvitationDialog } from './pages/contact/SendEmailInvitationDialog';
import EnhancedContentPage from './pages/enhanced-content/EnhancedContentPage.tsx';
import ExportPage from './pages/export/ExportPage.tsx';
import { ItemDetailsPage } from './pages/item-details/ItemDetailsPage.tsx';
import MonitorPage from './pages/monitor/MonitorPage';
import OrganizationPage from './pages/organization/OrganizationPage';
import { PublicationsPage } from './pages/publication';
import ExecutedPublication from './pages/publication/ExecutedPublication.tsx';
import UserPage from './pages/user/UserPage';
import AddUserGroupPage from './pages/user-groups/AddUserGroupPage.tsx';
import UserGroupsPage from './pages/user-groups/UserGroupsPage.tsx';
import UserManagementPage from './pages/user-management/UserManagementPage.tsx';
import { hasSettingFeature } from './services/angularjs-bridge';
import { ComponentProps } from './types/ComponentProps.ts';

// Extend the browser's native window object with properties that we want to expose
// from our React app to the outer (legacy) world.
export interface CustomWindow extends Window {
    reactCreateComponent: (
        container: Element | DocumentFragment,
        options?: RootOptions,
    ) => Root;

    // A collection for primitive components
    reactComponents: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (props: any) => JSX.Element;
    };

    // A collection for 'Page' components
    reactPages: {
        [key: string]: (props: ComponentProps) => JSX.Element;
    };

    // AngularJS publish actions (browse, editor grouping, publications re-run).
    openPublishToRecipients: typeof openPublishToRecipients;

    // A collection for 'Dialog' components
    reactDialogs: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (props: any) => JSX.Element;
    };

    // Base url used with our exposed open micro frontends
    __BYRD_BASE_URL__: string;
    // Marker used mainly for React.Portal
    __BYRD_MICRO_FRONTEND__: boolean;

    // Shared DB object with AngularJS app.
    __IDB: IDBDatabase;

    // Control flag over enabled logging streams (enabled on development)
    __DEBUG_LOGS_ENABLED?: boolean;
}

declare const window: CustomWindow;

// Attaching the createRoot to the global window instance to be used by the outer (legacy) world
window.reactCreateComponent = createRoot;
window.openPublishToRecipients = openPublishToRecipients;

// A collection of components that are attached to the global window instance
window.reactComponents = {};

// A collection of pages that are attached to the global window instance
window.reactPages = {};
window.reactPages['MonitorPage'] = () => (
    <ReactBridgePageWrapper>
        <MonitorPage />
    </ReactBridgePageWrapper>
);

window.reactPages['AdminSettingsPage'] = () => (
    <ReactBridgePageWrapper>
        <AdminSettingsPage />
    </ReactBridgePageWrapper>
);

window.reactPages['ExportPage'] = () => (
    <ReactBridgePageWrapper>
        <ExportPage />
    </ReactBridgePageWrapper>
);

window.reactPages['EnhancedContentPage'] = () =>
    <ReactBridgePageWrapper>
        <EnhancedContentPage />
    </ReactBridgePageWrapper>;

window.reactPages['BulkSubscriptionsPage'] = () =>
    <ReactBridgePageWrapper>
        <BulkSubscriptionsPage />
    </ReactBridgePageWrapper>;

window.reactPages['OrganizationPage'] = () => (
    <ReactBridgePageWrapper>
        <OrganizationPage />
    </ReactBridgePageWrapper>
);

window.reactPages['UserPage'] = () => (
    <ReactBridgePageWrapper>
        <UserPage />
    </ReactBridgePageWrapper>
);

window.reactPages['UserGroupsPage'] = () =>
    <ReactBridgePageWrapper>
        <UserGroupsPage />
    </ReactBridgePageWrapper>;

window.reactPages['AddUserGroupPage'] = () =>
    <ReactBridgePageWrapper>
        <AddUserGroupPage />
    </ReactBridgePageWrapper>;

window.reactPages['UserManagementPage'] = () => (
    <ReactBridgePageWrapper>
        <UserManagementPage />
    </ReactBridgePageWrapper>
);

window.reactPages['ContactsPage'] = () => (
    <ReactBridgePageWrapper>
        <ContactsPage />
    </ReactBridgePageWrapper>
);

// LAX-10137: /browse, /edit/*, and /copy/* mount BrowsePage only when
// FEATURE:NEXT_HIT_LIST is on. Otherwise those routes keep ItemsController.
// The getter stays lazy so organization settings are loaded before the check.
Object.defineProperty(window.reactPages, 'BrowsePage', {
    enumerable: true,
    configurable: true,
    get(): ((props?: BrowsePageRouteProps) => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_HIT_LIST')) {
            return undefined;
        }
        return (props) => (
            <ReactBridgePageWrapper>
                <FocusAttributeProvider>
                    <BrowsePage {...props} />
                </FocusAttributeProvider>
            </ReactBridgePageWrapper>
        );
    },
});

// LAX-10148: PublicationsPage composes scheduled + executed publications —
// same NEXT_PUBLICATIONS gate as publications.tpl.html. Evaluated lazily
// via a getter so organization settings are loaded before the check.
Object.defineProperty(window.reactPages, 'PublicationsPage', {
    enumerable: true,
    configurable: true,
    get(): (() => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_PUBLICATIONS')) {
            return undefined;
        }
        return () => (
            <ReactBridgePageWrapper>
                <PublicationsPage />
            </ReactBridgePageWrapper>
        );
    },
});

window.reactComponents['ExecutedPublication'] = (
    props: any,
) =>
    <ReactBridgePageWrapper>
        <ExecutedPublication {...props}/>
    </ReactBridgePageWrapper>;

// A collection of dialogs that are attached to the global window instance
window.reactDialogs = {};
window.reactDialogs['ExportMediaAssetDialog'] = (
    props: ExportMediaAssetDialogProps,
) => (
    <ReactBridgePageWrapper>
        <ExportMediaAssetDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['DataModelExchangeDialog'] = (
    props: DataModelExchangeDialogProps,
) => (
    <ReactBridgePageWrapper>
        <DataModelExchangeDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['PriorItemDialog'] = (props: PriorItemDialogProps) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <PriorItemDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['CommunicationChannelsErrorDialog'] = (
    props: CommunicationChannelsErrorDialogProps,
) => (
    <ReactBridgePageWrapper>
        <CommunicationChannelsErrorDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['CompareItemsDialog'] = (
    props: CompareItemsDialogProps,
) => (
    <ReactBridgePageWrapper>
        <CompareItemsDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['MassUpdateDialog'] = (
    props: MassUpdateDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <MassUpdateDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['ItemEditorDialog'] = (
    props: ItemEditorDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemEditorDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['ItemReviewsDialog'] = (
    props: ItemReviewsDialogProps,
) => (
    <ReactBridgePageWrapper>
        <ItemReviewsDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['ConfirmationModal'] = (
    props: any,
) => (
    <ReactBridgePageWrapper>
        <ConfirmationModal {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['CreateTaskDialog'] = (
    props: CreateTaskDialogProps,
) => (
    <ReactBridgePageWrapper>
        <CreateTaskDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['SendEmailInvitationDialog'] = (
    props: { open: boolean; onClose: () => void },
) => (
    <ReactBridgePageWrapper>
        <SendEmailInvitationDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['PublishToRecipientsDialog'] = (
    props: PublishToRecipientsDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <PublishToRecipientsDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

Object.defineProperty(window.reactDialogs, 'FilterBuilder', {
    enumerable: true,
    configurable: true,
    get(): ((props: FilterBuilderProps) => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_HIT_LIST')) {
            return undefined;
        }
        return (props: FilterBuilderProps) => (
            <ReactBridgePageWrapper>
                <FocusAttributeProvider>
                    <FilterBuilder {...props} />
                </FocusAttributeProvider>
            </ReactBridgePageWrapper>
        );
    },
});

window.reactComponents['ItemDetails'] = (
    props: ItemDetailsComponentProps
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemDetails {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactComponents['ItemDetailsPage'] = () => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemDetailsPage/>
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactComponents['ItemsList'] = (
    props: ItemsListComponentProps
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemsList {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

// LAX-10134: React Filters dropdown. Still mounted into `#search-filter-bar`
// by ItemsController when NEXT_HIT_LIST is on and the legacy shell is showing.
// The routed BrowsePage renders SearchFilterBar itself.
window.reactComponents['SearchFilterBar'] = () => (
    <ReactBridgePageWrapper>
        <SearchFilterBarHost />
    </ReactBridgePageWrapper>
);
